import { useMemo } from 'react';
import Hero from '@/pages/forex-analysis/components/Hero';
import SectionNav from '@/pages/forex-analysis/components/SectionNav';
import LearningPath from '@/pages/forex-analysis/components/LearningPath';
import TutorialLibrary from '@/pages/forex-analysis/components/TutorialLibrary';
import ChartCaseStudy from '@/pages/forex-analysis/components/ChartCaseStudy';
import IndicatorShowcase from '@/pages/forex-analysis/components/IndicatorShowcase';
import AnalysisMistakes from '@/pages/forex-analysis/components/AnalysisMistakes';
import RelatedTracks from '@/pages/forex-analysis/components/RelatedTracks';
import { siteOrigin, useSeo } from '@/hooks/useSeo';
import { analysisCategories } from '@/mocks/forexAnalysisPage';

export default function ForexAnalysis() {
  const jsonLd = useMemo(() => {
    const origin = siteOrigin();
    const lessons = analysisCategories.flatMap((category) =>
      category.lessons
        .filter((lesson) => Boolean(lesson.slug))
        .map((lesson) => ({
          '@type': 'Article',
          headline: lesson.title,
          url: `${origin}/${lesson.slug}`,
        })),
    );

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'หน้าแรก', item: `${origin}/` },
          { '@type': 'ListItem', position: 2, name: 'วิเคราะห์ Forex', item: `${origin}/forex-analysis` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'เรียนรู้การวิเคราะห์ Forex สำหรับมือใหม่',
        description:
          'คลังบทความการวิเคราะห์ Forex ภาษาไทย แบ่งเป็นหกหมวด ตั้งแต่แท่งเทียน แนวโน้ม แนวรับแนวต้าน อินดิเคเตอร์ รูปแบบกราฟ ไปจนถึงเทคนิคการวิเคราะห์อย่างเป็นระบบ',
        inLanguage: 'th-TH',
        url: `${origin}/forex-analysis`,
        hasPart: lessons,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'LearningResource',
        name: 'เส้นทางการเรียนรู้การวิเคราะห์ Forex',
        description:
          'เส้นทางการเรียนรู้สี่ขั้นตอนสำหรับมือใหม่ ตั้งแต่พื้นฐานกราฟและแท่งเทียน ไปจนถึงการวิเคราะห์กราฟอย่างเป็นระบบ',
        inLanguage: 'th-TH',
        educationalLevel: 'Beginner',
        learningResourceType: 'Course',
        isAccessibleForFree: true,
        url: `${origin}/forex-analysis#learning-path`,
      },
    ];
  }, []);

  useSeo({
    /* @replace:analysis-title */
    title: 'วิเคราะห์ Forex สำหรับมือใหม่: กราฟ แนวโน้ม และอินดิเคเตอร์',
    description:
      /* @replace:analysis-description */
      'เรียนรู้การวิเคราะห์ Forex สำหรับมือใหม่ ตั้งแต่การอ่านกราฟแท่งเทียน การดูแนวโน้ม แนวรับแนวต้าน ไปจนถึงการใช้ Moving Average, RSI และ MACD',
    /* @replace:analysis-keywords */
    keywords: 'วิเคราะห์ Forex, กราฟแท่งเทียน, Price Action, Moving Average, RSI, MACD, แนวรับแนวต้าน, Technical Analysis',
    canonicalPath: '/forex-analysis',
    jsonLd,
  });

  return (
    <div className="bg-background-50 -mb-20 md:-mb-28">
      <Hero />
      <SectionNav />
      <LearningPath />
      <TutorialLibrary />
      <ChartCaseStudy />
      <IndicatorShowcase />
      <AnalysisMistakes />
      <RelatedTracks />
    </div>
  );
}